import React, { useEffect, useRef, useState } from "react";
import { Chip, Menu, MenuItem, ListItemIcon, Icon, Typography } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert"; // Menu icon
import DeleteIcon from "@mui/icons-material/Delete";
// import CloseIcon from "@mui/icons-material/Close";
// import CancelIcon from "@mui/icons-material/Cancel";

import dayjs from "dayjs";
import "dayjs/locale/nl"; // Import Dutch locale

dayjs.locale("nl");

interface FilterOverviewProps {
  search: string;
  startDate: string;
  endDate: string;
  matchExactRange: boolean;
  filterData?: any;
  filters: Record<string, any[]>;
  onDelete: (key: string, value: string) => void;
}

const FilterOverview: React.FC<FilterOverviewProps> = ({
  search,
  startDate,
  endDate,
  matchExactRange,
  filterData,
  filters,
  onDelete,
}) => {
  const formatDate = (date: string) => dayjs(date).format("D MMMM YYYY");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentFilterKey, setCurrentFilterKey] = useState<string | null>(null);

  const [disciplines, setDisciplines] = useState<any[]>([]);
  const [statusses, setStatusses] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [organisaties, setOrganisaties] = useState<any[]>([]);
  const [beoordeling, setBeoordeling] = useState<any[]>([]);

  // Ref to prevent fetching data twice in Strict Mode
  const hasFetchedData = useRef(false);

  useEffect(() => {
    // Ensure the fetch happens only once
    if (!hasFetchedData.current && filterData && Object.keys(filterData).length > 0) {
      setFilterData(filterData);
      hasFetchedData.current = true; // Mark as fetched
    }
  }, [filterData]); // Empty dependency array ensures this runs once when the component mounts

  const setFilterData = (data: any) => {
    const filters = data;
    if (filters.discipline) {
      setDisciplines(filters.discipline);
    }

    if (filters.planStatus) {
      setStatusses(filters.planStatus);
    }

    if (filters.planType) {
      setTypes(filters.planType);
    }

    if (filters.organizations) {
      setOrganisaties(filters.organizations);
    }

    if (filters.infplBeoordeling) {
      setBeoordeling(filters.infplBeoordeling);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, key: string) => {
    setAnchorEl(event.currentTarget);
    setCurrentFilterKey(key);
  };

  const getLookupArray = (key: string) => {
    switch (key) {
      case 'discipline':
        return disciplines;
      case 'status':
        return statusses;
      case 'type':
        return types;
      case 'organisatie':
        return organisaties;
      case 'beoordeling':
        return beoordeling;
      default:
        return [];
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentFilterKey(null);
  };

  const capitalizeFirstLetter = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  return (
    <>
      {(startDate || endDate || Object.values(filters).some(arr => arr.length > 0)) && (
        <div className="filter-overview">
          {/* Date filters */}
          {startDate && endDate ? (
            <Chip
              label={
                <span>
                  {matchExactRange && (<b>Volledig binnen bereik</b>)} <strong>van</strong> {formatDate(startDate)} <strong>tot en met</strong> {formatDate(endDate)} 
                </span>
              }
              onClick={(e) => handleMenuOpen(e as any, "date")}
              deleteIcon={<MoreVertIcon />}
              onDelete={(e) => handleMenuOpen(e as any, "date")}
            />
          ) : startDate ? (
            <Chip
              label={
                <span>
                  {matchExactRange && (<b>Volledig binnen bereik</b>)} <strong>van</strong> {formatDate(startDate)}
                </span>
              }
              deleteIcon={<DeleteIcon />}
              onDelete={() => onDelete("from", startDate)}
            />
          ) : endDate ? (
            <Chip
              label={
                <span>
                  {matchExactRange && (<b>Volledig binnen bereik</b>)} <strong>tot en met</strong> {formatDate(endDate)}
                </span>
              }
              deleteIcon={<DeleteIcon />}
              onDelete={() => onDelete("to", endDate)}
            />
          ) : null}

          {/* Filter opties */}
          {Object.entries(filters)
            .filter(([_, values]) => values.length > 0) // Hide empty filters
            .map(([key, values]) => {
              const lookup = getLookupArray(key);
          
              const getNameById = (id: any) => {
                const match = lookup.find((item) => item.id === id);
                return match?.name || id;
              };
          
              const renderedValues = values.map(getNameById).join(', ');

              return values.length === 1 ? (
                <Chip key={key} 
                  label={
                    <Typography variant="body2">
                      <strong>{capitalizeFirstLetter(key)}</strong>: {renderedValues}
                    </Typography>
                  }
                  deleteIcon={<DeleteIcon />}
                  onDelete={() => onDelete(key, values[0])} />
              ) : (
                <Chip
                  key={key}
                  label={
                    <Typography variant="body2">
                      <strong>{capitalizeFirstLetter(key)}</strong>: {renderedValues}
                      {/* <strong>{capitalizeFirstLetter(key)}</strong>: {values.length} geselecteerd */}
                    </Typography>
                  }
                  deleteIcon={<MoreVertIcon />}
                  onDelete={(e) => handleMenuOpen(e as any, key)}
                />
              )
            })}

          {/* Verwijder alle filters */}
          {(startDate || endDate || Object.values(filters).some(arr => arr.length > 0)) && (
            <Chip
              label="Verwijder alle filters"
              color="secondary"
              onDelete={() => onDelete("all", "")}
              deleteIcon={<DeleteIcon />}
            />
          )}

          {/* MUI Menu voor multi filters */}
          <Menu 
            className="navigation nav-menu"
            anchorEl={anchorEl} 
            open={Boolean(anchorEl)} 
            onClose={handleMenuClose}>
            {currentFilterKey === "date" ? (
              <>
                {startDate && (
                  <MenuItem
                    onClick={() => {
                      onDelete("from", startDate);
                      handleMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <Icon>delete</Icon>
                    </ListItemIcon>
                    Van {formatDate(startDate)}
                  </MenuItem>
                )}
                {endDate && (
                  <MenuItem
                    onClick={() => {
                      onDelete("to", endDate);
                      handleMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <Icon>delete</Icon>
                    </ListItemIcon>
                    Tot en met {formatDate(endDate)}
                  </MenuItem>
                )}
              </>
            ) : (
              filters[currentFilterKey || ""]?.map((value) => {
                const lookup = currentFilterKey ? getLookupArray(currentFilterKey) : [];

                const getNameById = (id: any) => {
                  const match = lookup.find((item) => item.id === id);
                  return match?.name || id;
                };
                
                return (
                  <MenuItem
                    key={value}
                    onClick={() => {
                      onDelete(currentFilterKey!, value);
                      handleMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <Icon>delete</Icon>
                    </ListItemIcon>
                    {getNameById(value)}
                  </MenuItem>
                );
              })
            )}
          </Menu>
        </div>
      )}
    </>
  );
};

export default FilterOverview;
